// 錄影設定 — ADR-046 分流 (spec 21 §6):
//   record_mode / record_location / record_quality：
//     讀 → GET /v3/devices/{id}/settings（快照）
//     寫 → LS 自家 app 走 ws op:set（enumVal:int）；restful PUT 保留給 3rd-party。
//     (op:set→device ACK→DDB→屬性變更回報收斂; enum id 送 enumVal，signaling REJECT intVal)
//   RecordStatus (STAT) + ManualRecordLength (device knob) → WSS op model.
// Waiting UX per §6.2: lock pickers + spinner; >3s「等待裝置回應…」; client cap 10s
// → unlock + 「設定未確認，請重試」+ revert; late ACK reconciles silently.
// sdcard 選擇 → 先彈「雲端回放將停用」confirm，取消時零 wire 流量 (AC-DS-15)。

import { useCallback, useEffect, useRef, useState, useContext } from 'react';
import { PhoneCamContext, usePhoneT } from '../../../lib/phoneCam';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { TFunction } from 'i18next';
import { addSubscriptionDevice, ApiError, apiFetch, getDeviceEntitlements, listSubscriptions } from '../../../lib/api';
import type { EffectiveEntitlementExplain, UserSubscriptionView } from '../../../lib/api';
import { cloudSetupIntent } from './cloudSetupIntent';
import { planRecordingModes } from './recordingEntitlement';
import { signaling } from '../../../lib/ws';
import type { SignalFrame } from '../../../lib/ws';
import type { DeviceChannel } from '../deviceModel';
import {
  isRecordSettingReport,
  recQualityEnum,
  REC_LOCATION,
  REC_LOCATION_ENUM,
  REC_MANUAL_LENGTH,
  REC_MODE,
  REC_MODE_ENUM,
  REC_QUALITY,
  REC_SCHEDULE,
  REC_STATUS,
} from './ids';
import { opSetEnum, useAttr } from './ops';
import { ConfirmDialog, ReadonlyRow, SectionCard } from './widgets';
import { WeeklyScheduleEditor } from './WeeklyScheduleEditor';

interface RecordingSettings {
  record_mode: string;
  record_location: string;
  record_quality: string;
  backup: RecordingBackupSettings;
}

interface RecordingBackupSettings {
  enabled: boolean;
  type: string;
  host: string;
  port: number;
  remote_dir: string;
  username: string;
  server_name: string;
  disable_epsv: boolean;
  password_configured: boolean;
}

type RecordingField = 'record_mode' | 'record_location' | 'record_quality';

// ⚠️ i18n: 選項顯示名須在使用點以 t 求值(語言切換要即時),不可凍結成模組頂層 const。
// 第一元素 = wire value(別動),第二 = 顯示名。畫質為純數值名(非中文)→ 不抽。
const modeOptions = (t: TFunction): Array<[string, string]> => [
  ['continuous', t('deviceSettings.recordModeContinuous')],
  ['event', t('deviceSettings.recordModeEvent')],
  ['none', t('deviceSettings.recordModeNone')],
];
const locationOptions = (t: TFunction): Array<[string, string]> => [
  ['cloud', t('deviceSettings.recordLocationCloud')],
  ['sdcard', t('deviceSettings.recordLocationSdcard')],
];
// spec 0x8050 RecordQuality 值集:720p/1080p/2k/4k(無 480p)。順序對齊 enum 0→3。
const QUALITY_OPTIONS: Array<[string, string]> = [
  ['720p', '720p'],
  ['1080p', '1080p'],
  ['2k', '2K'],
  ['4k', '4K'],
];
// 畫質排名(720p=0 最低 … 4k=3 最高)。-1 = 未知/未設 → 不設限。對齊 iOS qualityRank / Android recQualityEnum。
const qualityRank = (q?: string): number => (q ? QUALITY_OPTIONS.findIndex(([v]) => v === q.toLowerCase()) : -1);
const BACKUP_TYPES = (t: TFunction): Array<[string, string]> => [
  ['ftp', t('deviceSettings.recordBackupTypeFtp')],
  ['ftps', t('deviceSettings.recordBackupTypeFtps')],
  ['localfs', t('deviceSettings.recordBackupTypeLocalfs')],
];

function emptyBackup(): RecordingBackupSettings {
  return {
    enabled: false,
    type: 'none',
    host: '',
    port: 0,
    remote_dir: '',
    username: '',
    server_name: '',
    disable_epsv: false,
    password_configured: false,
  };
}

// 取第一個「非空字串」(空字串視同未設 → 套用預設)。後端對未設定錄影的裝置會回空字串
// (record_mode:"" 等,非缺鍵),若不歸零會讓三個 picker 顯示空白(無選取值)。spec 21 §6.2。
function firstNonEmpty(...vals: unknown[]): string | undefined {
  for (const v of vals) if (typeof v === 'string' && v !== '') return v;
  return undefined;
}

function parseBackup(raw: unknown): RecordingBackupSettings {
  if (!raw || typeof raw !== 'object') return emptyBackup();
  const o = raw as Record<string, unknown>;
  const port = typeof o.port === 'number' ? o.port : Number(o.port || 0) || 0;
  return {
    enabled: o.enabled === true,
    type: firstNonEmpty(o.type) ?? 'none',
    host: firstNonEmpty(o.host) ?? '',
    port,
    remote_dir: firstNonEmpty(o.remote_dir) ?? '',
    username: firstNonEmpty(o.username) ?? '',
    server_name: firstNonEmpty(o.server_name) ?? '',
    disable_epsv: o.disable_epsv === true,
    password_configured: o.password_configured === true,
  };
}

interface ParsedSettings {
  settings: RecordingSettings;
  // DDB 尚未實際存值(空字串/缺鍵)的業務欄位 → 畫面顯示的是「合成預設」，並未真正下發到裝置。
  // 這些欄位在 submit 時必須一併 op:set(即使與畫面現值相同),否則裝置會停在自己的韌體預設
  // ——location 韌體預設是 cloud(record.c)、畫面合成預設是 sdcard,兩者不符會造成
  // 「畫面顯示 sdcard、實際錄到 cloud」的不一致(spec ① / ADR-046)。
  unset: Set<RecordingField>;
}

function parseSettings(raw: unknown): ParsedSettings | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  // DDB snapshot shape per restful 00 §7; tolerate the §6.1 nested `recording` shape too.
  const nested = o.recording && typeof o.recording === 'object' ? (o.recording as Record<string, unknown>) : null;
  const mode = firstNonEmpty(o.record_mode, nested?.mode);
  const location = firstNonEmpty(o.record_location, nested?.location);
  const quality = firstNonEmpty(o.record_quality, nested?.quality);
  // 任一欄存在(含空字串會被視為未設但仍代表這是 settings 物件)→ 視為有效快照;全缺才回 null。
  const present =
    'record_mode' in o || 'record_location' in o || 'record_quality' in o || nested != null;
  if (!present) return null;
  // 空字串/缺鍵 → firstNonEmpty 回 undefined → 該欄「未持久化」,記入 unset 供 submit 補寫。
  const unset = new Set<RecordingField>();
  if (mode === undefined) unset.add('record_mode');
  if (location === undefined) unset.add('record_location');
  if (quality === undefined) unset.add('record_quality');
  // 新裝置預設(spec ①):mode=none / location=sdcard / quality=720p。
  return {
    settings: {
      record_mode: mode ?? 'event',
      record_location: location ?? 'sdcard',
      record_quality: quality ?? '720p',
      backup: parseBackup(o.recording_backup ?? nested?.backup),
    },
    unset,
  };
}

type PendingPhase = 'idle' | 'waiting' | 'waiting-long';

// §3.7「設定 cloud 錄影 → 加入 membership plan」狀態機（D19-a..d）。
// 觸發＝把 record_location 從非 cloud 改成 cloud；attach 成功才落 cloud 設定。
const CLOUD_ACTIVE = new Set(['active', 'trialing', 'grace']); // §8 名下可用 membership（§3.5 服務仍有效態，三平台一致）
type CloudFlow =
  | { step: 'checking'; next: RecordingSettings } // GET /v3/subscriptions 進行中
  | { step: 'pick'; next: RecordingSettings; subs: UserSubscriptionView[] } // 多個可加入方案 → 讓使用者選
  | { step: 'confirm'; next: RecordingSettings; sub: UserSubscriptionView } // 詢問「加入雲端方案？」
  | { step: 'attaching'; next: RecordingSettings; sub: UserSubscriptionView }; // POST attach 進行中

export function RecordingSection({ deviceId, canEdit, wsOpen, channels = [], maxResolution }: { deviceId: string; canEdit: boolean; wsOpen: boolean; channels?: DeviceChannel[]; maxResolution?: string }) {
  const { t } = usePhoneT();
  const phone = useContext(PhoneCamContext);   // 手機攝影機：「SD 卡」→「手機空間」（ls-loop MW）
  // last-ACK snapshot (= GET snapshot or last 200 response; §6.2 rollback target)
  const [snapshot, setSnapshot] = useState<RecordingSettings | null>(null);
  // 業務欄位中「DDB 尚未持久化」者(見 ParsedSettings.unset)。submit 會強制補寫這些欄位,
  // 讓首次任何一項變更就把三欄位一併同步到裝置/DDB,消除「畫面預設 vs 韌體預設」的落差。
  const [unset, setUnset] = useState<Set<RecordingField>>(() => new Set());
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [phase, setPhase] = useState<PendingPhase>('idle');
  const [notice, setNotice] = useState<string | null>(null); // toast-ish inline message
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [planFull, setPlanFull] = useState(false); // 方案名額滿 → 錯誤旁邊給「管理方案」連結
  const [sdConfirm, setSdConfirm] = useState<RecordingSettings | null>(null); // pending PUT awaiting sdcard confirm
  const [cloudFlow, setCloudFlow] = useState<CloudFlow | null>(null); // §3.7 cloud→membership state machine
  // R3: plan-effective entitlements → constrains the record MODE picker (recording_modes).
  // null = unknown/incomplete → no constraint (full static list). Refetched after any
  // location-changing submit and after the R1/R2 cloud resume.
  const [explain, setExplain] = useState<EffectiveEntitlementExplain | null>(null);
  const navigate = useNavigate();
  const loc = useLocation();
  const [backupDraft, setBackupDraft] = useState<RecordingBackupSettings>(() => emptyBackup());
  const [backupPassword, setBackupPassword] = useState('');
  const [backupSaving, setBackupSaving] = useState(false);
  const [backupNotice, setBackupNotice] = useState<string | null>(null);
  const [backupError, setBackupError] = useState<string | null>(null);
  const attempt = useRef(0);
  const alive = useRef(true);

  const recStatus = useAttr(deviceId, REC_STATUS, wsOpen);
  const manualLen = useAttr(deviceId, REC_MANUAL_LENGTH, wsOpen && canEdit);
  const [manualDraft, setManualDraft] = useState<string>('');

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // background=true（事件觸發的 refresh）：保留現有 snapshot 繼續顯示，不閃 loading；
  // 失敗也不降級成 error card（設定其實已生效，只是 refetch 剛好失敗）。
  const load = useCallback(
    (background = false) => {
      if (!background) setLoadState('loading');
      apiFetch<unknown>(`/v3/devices/${deviceId}/settings`).then(
        (res) => {
          if (!alive.current) return;
          const parsed = parseSettings(res.data);
          setSnapshot(parsed?.settings ?? null);
          setUnset(parsed?.unset ?? new Set());
          if (parsed) {
            setBackupDraft(parsed.settings.backup);
            setBackupPassword('');
          }
          setLoadState('ready');
        },
        () => {
          if (!alive.current) return;
          if (!background) setLoadState('error');
        }
      );
    },
    [deviceId]
  );

  useEffect(() => {
    load();
  }, [load]);

  // R3: fetch plan-effective entitlements (recording_modes gate the MODE picker). Best-effort:
  // any failure → null (= no constraint). Callable again after a location-changing submit and
  // after the R1/R2 cloud resume so switching cloud↔sdcard refreshes the allowed modes.
  const loadEntitlements = useCallback(() => {
    getDeviceEntitlements(deviceId).then(
      (e) => {
        if (alive.current) setExplain(e);
      },
      () => {
        if (alive.current) setExplain(null);
      }
    );
  }, [deviceId]);

  useEffect(() => {
    loadEntitlements();
  }, [loadEntitlements]);

  // 屬性變更回報（ADR-046 / common/04 §5.11）：device 在值有變的 op:set 成功後主動廣播
  // op:report（無 request_id，id = 三錄影 attr 之一，no-op 不發）→ ws.ts 以 'event' 上拋。
  // 收到本裝置這三 id 的回報即 refetch REST 快照收斂顯示（他端 op:set / signin_sync 亦同步）。
  // room 訂閱由 DeviceSettings 的 useDeviceRoom 持有，這裡只掛 listener。
  useEffect(() => {
    const off = signaling.on('event', (frame: SignalFrame) => {
      if (frame.device_id !== deviceId || !isRecordSettingReport(frame)) return;
      if (!alive.current) return;
      load(true);
      setNotice(t('deviceSettings.settingSynced'));
    });
    return off;
  }, [deviceId, load, t]);

  // §6.2 / ADR-046: not optimistic — lock, fire ws op:set per changed attr
  // (enumVal:int; signaling REJECTs intVal for these enum ids), wait for all ACKs.
  const submit = useCallback(
    (next: RecordingSettings) => {
      const base = snapshot;
      if (!base) return;
      // 對「與現快照不同」或「尚未持久化(unset)」的欄位下 op:set；enum id → enumVal。
      // unset 欄位即使值與畫面現值相同也要補寫,否則裝置停在韌體預設(如 location=cloud),
      // 造成畫面(sdcard)與實錄不一致。首次任一變更即把三欄位收斂到裝置/DDB。
      const writes: Array<Promise<SignalFrame>> = [];
      if (next.record_mode !== base.record_mode || unset.has('record_mode')) {
        const n = REC_MODE_ENUM[next.record_mode];
        if (n !== undefined) writes.push(opSetEnum(deviceId, REC_MODE, n));
      }
      if (next.record_location !== base.record_location || unset.has('record_location')) {
        const n = REC_LOCATION_ENUM[next.record_location];
        if (n !== undefined) writes.push(opSetEnum(deviceId, REC_LOCATION, n));
      }
      if (next.record_quality !== base.record_quality || unset.has('record_quality')) {
        const n = recQualityEnum(next.record_quality);
        if (n !== undefined) writes.push(opSetEnum(deviceId, REC_QUALITY, n));
      }
      if (writes.length === 0) return; // no-op change → zero wire traffic

      const myAttempt = ++attempt.current;
      setPhase('waiting');
      setNotice(null);
      setFieldError(null);
      setPlanFull(false);
      const longTimer = setTimeout(() => {
        if (alive.current && attempt.current === myAttempt) setPhase('waiting-long');
      }, 3000);
      // client waiting cap 10s (earlier than the backend/ws 30s timeout)
      const capTimer = setTimeout(() => {
        if (!alive.current || attempt.current !== myAttempt) return;
        attempt.current++; // mark this attempt as timed-out → late ACKs reconcile silently
        setPhase('idle');
        setNotice(t('deviceSettings.settingUnconfirmed'));
        // revert display to the last-ACK snapshot (state already shows snapshot — nothing else to do)
      }, 10_000);

      Promise.all(writes).then(
        () => {
          clearTimeout(longTimer);
          clearTimeout(capTimer);
          if (!alive.current) return;
          // optimistic reconcile to `next`, then refetch GET /settings to收斂真值
          // (屬性變更回報 listener 也會 refetch — 兩者皆冪等)。
          setSnapshot(next);
          setUnset(new Set()); // 三欄位已(或即將)持久化 → 清除補寫標記;refetch 會再校正。
          load(true);
          // R3: a location change can accompany a plan membership change (cloud attach path)
          // → refresh the entitlements that gate the MODE picker. Harmless no-op otherwise.
          if (next.record_location !== base.record_location) loadEntitlements();
          if (attempt.current === myAttempt) {
            setPhase('idle');
            setNotice(t('deviceSettings.settingApplied'));
          }
        },
        (err: unknown) => {
          clearTimeout(longTimer);
          clearTimeout(capTimer);
          if (!alive.current || attempt.current !== myAttempt) return; // late error → ignore (cap toast already shown)
          setPhase('idle');
          const msg = err instanceof Error ? err.message : '';
          if (/offline/i.test(msg)) {
            setFieldError(t('deviceSettings.recordErrOffline', { msg }));
          } else {
            setFieldError(t('deviceSettings.recordErrNetwork'));
          }
          // rollback = keep rendering `snapshot` (last ACK)
        }
      );
    },
    [deviceId, snapshot, unset, load, loadEntitlements, t]
  );

  // 切到 cloud 時依方案調整 next(對齊 iOS confirmCloudAttach / Android adjustForCloud):抓最新權益 →
  //   mode 無效(none 或不在方案允許集)→ 挑預設(event 優先,其次 continuous);
  //   畫質超過方案 quality_max → 降到最低 720p。已有效的 mode / 未超標的畫質保留;方案未知 → 不動。
  const adjustForCloud = useCallback(
    async (next: RecordingSettings): Promise<RecordingSettings> => {
      let ent: EffectiveEntitlementExplain | null = null;
      try {
        ent = await getDeviceEntitlements(deviceId);
      } catch {
        ent = null;
      }
      if (alive.current && ent) setExplain(ent);
      const modes = planRecordingModes(ent);
      let mode = next.record_mode;
      if (mode === 'none' || (modes != null && !modes.includes(mode))) {
        if (modes?.includes('event')) mode = 'event';
        else if (modes?.includes('continuous')) mode = 'continuous';
      }
      let quality = next.record_quality;
      const cap = qualityRank(ent?.entitlements?.quality_max);
      if (cap >= 0 && qualityRank(quality) > cap) quality = '720p';
      return { ...next, record_mode: mode, record_quality: quality };
    },
    [deviceId]
  );

  // §3.7 step 0: user set record_location=cloud → ensure the device is in a plan
  // BEFORE persisting the setting. Runs GET /v3/subscriptions then branches per the
  // state machine (D19-a..d). No wire write to the device happens until attach是 OK。
  const startCloudFlow = useCallback(
    async (next: RecordingSettings) => {
      setCloudFlow({ step: 'checking', next });
      setNotice(null);
      setFieldError(null);
      setPlanFull(false);
      let subs: UserSubscriptionView[];
      try {
        subs = await listSubscriptions();
      } catch (err) {
        if (!alive.current) return;
        setCloudFlow(null);
        setFieldError(err instanceof ApiError && err.message ? err.message : t('deviceSettings.recordErrNetwork'));
        return; // 查詢失敗 → 還原（picker 續顯示 snapshot）
      }
      if (!alive.current) return;
      const active = subs.filter((s) => CLOUD_ACTIVE.has(s.status));
      if (active.length === 0) {
        // R1 gap: 無 active 訂閱 → 導向帳號層購買/領取頁(/account/subscription),並「記住意圖」:
        // 在 account 頁 attach 這台裝置後(或買/領方案後回到本頁)自動收斂成 location=cloud。
        // sessionStorage 撐過 Stripe checkout 全頁跳轉的來回;router state 撐同一 SPA 的 attach→返回。
        setCloudFlow(null);
        cloudSetupIntent.set(deviceId);
        setNotice(t('deviceSettings.cloudSetupRedirect'));
        navigate('/account/subscription');
        return;
      }
      // D19-a：device 已在某方案 devices[] 內 → 跳過 attach，直接落設定
      if (active.some((s) => (s.devices ?? []).includes(deviceId))) {
        setCloudFlow(null);
        submit(await adjustForCloud(next)); // D19-a 已在方案內 → 依方案調整 mode/畫質後落設定
        return;
      }
      // 可加入 = active 且未滿(已在某方案 → 上方 D19-a 已處理)。多個 → 讓使用者選要加入哪個(修正:
      // 先前硬取 active[0] 直接加入,不符 spec R1「顯示 plan list」);剛好一個 → 直接詢問;全滿 → 警告還原。
      const joinable = active.filter((s) => (s.device_count ?? 0) < (s.max_devices ?? 0));
      if (joinable.length === 0) {
        setCloudFlow(null);
        // 寫清楚哪個方案、用了幾台、綁了誰——不然看起來就是「選雲端、放開又跳回 SD 卡」
        const plans = active.map((s) => `${s.plan_id} ${s.device_count ?? 0}/${s.max_devices ?? 0}：${(s.devices ?? []).join('、')}`).join('；');
        setFieldError(t('deviceSettings.cloudPlanFullDetail', { plans }));
        setPlanFull(true);
        return;
      }
      if (joinable.length === 1) {
        setCloudFlow({ step: 'confirm', next, sub: joinable[0] });
        return;
      }
      setCloudFlow({ step: 'pick', next, subs: joinable });
    },
    [deviceId, navigate, submit, adjustForCloud, t]
  );

  // R1 resume: the user picked cloud with no plan (→ /account/subscription) or attached the
  // device from the account page (→ back here with router state). Either signal — sessionStorage
  // intent surviving the Stripe round-trip, or the same-SPA location.state — resumes the flow once
  // the snapshot is loaded, driving it to record_location=cloud via the UNCHANGED D19 state machine
  // (device now in a plan's devices[] → D19-a → submit; else attach-confirm / full-error). A ref
  // fires it exactly once per mount so the submit-refetch churn cannot re-enter it; the intent is
  // cleared and the router state consumed on that single pass.
  const resumedRef = useRef(false);
  useEffect(() => {
    if (!snapshot || resumedRef.current) return;
    const stateResume = (loc.state as { applyCloudRecording?: unknown } | null)?.applyCloudRecording === true;
    const intentResume = cloudSetupIntent.get() === deviceId;
    if (!stateResume && !intentResume) return;
    resumedRef.current = true;
    cloudSetupIntent.clear();
    if (loc.state) navigate(loc.pathname, { replace: true, state: {} }); // consume the SPA hop
    if (snapshot.record_location !== 'cloud') {
      void startCloudFlow({ ...snapshot, record_location: 'cloud' });
      loadEntitlements(); // plan membership may have changed → refresh the MODE constraint
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshot, loc.state, deviceId]);

  // §3.7 "Yes" branch: POST attach (Idempotency-Key built into addSubscriptionDevice),
  // then on success call submit(next) to write record_location=cloud (DEC-09 synchronous).
  const attachAndApply = useCallback(async () => {
    setCloudFlow((cur) => (cur && cur.step === 'confirm' ? { step: 'attaching', next: cur.next, sub: cur.sub } : cur));
    const flow = cloudFlow;
    if (!flow || flow.step !== 'confirm') return;
    const { next, sub } = flow;
    try {
      await addSubscriptionDevice(sub.subscription_id, deviceId);
    } catch (err) {
      if (!alive.current) return;
      setCloudFlow(null); // 任何 attach 失敗 → 還原 record mode
      if (err instanceof ApiError && (err.code === 'SUBSCRIPTION_DEVICE_LIMIT' || err.status === 409)) {
        setFieldError(t('deviceSettings.cloudPlanFull'));
        setPlanFull(true);
      } else if (err instanceof ApiError && err.code === 'DEVICE_IN_OTHER_PLAN') {
        setFieldError(t('deviceSettings.cloudDeviceInOtherPlan'));
      } else {
        setFieldError(err instanceof ApiError && err.message ? err.message : t('deviceSettings.recordErrNetwork'));
      }
      return;
    }
    if (!alive.current) return;
    // ③ attach 成功（回 enriched view 含 devices[]）。本頁不顯示 plan 裝置清單 UI（該清單
    // 在 SubscriptionPage membership 區），此處無額外清單可更新；重整該頁時 GET 會取到最新。
    setCloudFlow(null);
    // ① 落 cloud 設定（走既有 WS op:set 寫入路徑；② device 由此路徑收到通知）。attach 後方案生效 →
    // 依方案調整 mode/畫質再落（對齊 iOS/Android）。D19-c：若這步失敗,submit 只顯示錯誤並續顯示原值,不自動 detach。
    submit(await adjustForCloud(next));
  }, [cloudFlow, deviceId, submit, adjustForCloud, t]);

  const onChangeField = useCallback(
    (field: RecordingField, value: string) => {
      if (!snapshot) return;
      const next = { ...snapshot, [field]: value };
      if (field === 'record_location' && value === 'sdcard' && snapshot.record_location !== 'sdcard') {
        // §6.2 / AC-DS-15: confirm BEFORE any wire traffic
        setSdConfirm(next);
        return;
      }
      if (field === 'record_location' && value === 'cloud' && snapshot.record_location !== 'cloud') {
        // §3.7: intercept BEFORE persisting — attach to a membership first.
        void startCloudFlow(next);
        return;
      }
      submit(next);
    },
    [snapshot, submit, startCloudFlow]
  );

  const locked = phase !== 'idle' || cloudFlow != null; // §3.7: lock pickers through the cloud flow too
  const backupLocked = backupSaving || !canEdit;

  if (loadState === 'loading') {
    return (
      <SectionCard title={t('deviceSettings.recording')}>
        <p className="text-secondary" style={{ margin: 0 }}>
          {t('common.loading')}
        </p>
      </SectionCard>
    );
  }
  if (loadState === 'error' || !snapshot) {
    return (
      <SectionCard title={t('deviceSettings.recording')}>
        <p className="error-text" style={{ margin: 0 }}>
          {t('deviceSettings.recordingLoadFailed')}
        </p>
        <button className="btn-ghost" style={{ justifySelf: 'start' }} onClick={() => load()}>
          {t('common.retry')}
        </button>
      </SectionCard>
    );
  }

  const picker = (label: string, field: RecordingField, options: Array<[string, string]>) => (
    <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacing-6)', padding: 'var(--spacing-3) 0' }}>
      <span>{label}</span>
      {canEdit ? (
        <select value={snapshot[field]} disabled={locked} onChange={(e) => onChangeField(field, e.target.value)} style={{ minWidth: 140 }}>
          {options.map(([v, text]) => (
            <option key={v} value={v}>
              {text}
            </option>
          ))}
          {!options.some(([v]) => v === snapshot[field]) && <option value={snapshot[field]}>{snapshot[field]}</option>}
        </select>
      ) : (
        // shared-user：唯讀現值（§6.2 授權：PUT owner-only）
        <span className="text-secondary">{options.find(([v]) => v === snapshot[field])?.[1] ?? snapshot[field]}</span>
      )}
    </label>
  );

  const backupTypeOptions = BACKUP_TYPES(t);
  const setBackupField = <K extends keyof RecordingBackupSettings>(field: K, value: RecordingBackupSettings[K]) => {
    setBackupDraft((prev) => ({ ...prev, [field]: value }));
    setBackupError(null);
    setBackupNotice(null);
  };
  const saveBackup = () => {
    if (!canEdit) return;
    const type = backupDraft.enabled
      ? (backupDraft.type === 'none' ? 'ftp' : backupDraft.type || 'ftp')
      : (backupDraft.type === 'none' ? 'none' : backupDraft.type || 'none');
    const passwordRaw = backupPassword;
    const password = passwordRaw.trim();
    if (backupDraft.enabled && (type === 'ftp' || type === 'ftps') && !backupDraft.password_configured && password === '') {
      setBackupError(t('deviceSettings.recordBackupPasswordRequired'));
      return;
    }
    setBackupSaving(true);
    setBackupNotice(null);
    setBackupError(null);
    const body: Record<string, unknown> = {
      enabled: backupDraft.enabled,
      type,
      host: backupDraft.host,
      port: backupDraft.port || 0,
      remote_dir: backupDraft.remote_dir,
      username: backupDraft.username,
      server_name: backupDraft.server_name,
      disable_epsv: backupDraft.disable_epsv,
    };
    if (password !== '') body.password = passwordRaw;
    apiFetch<RecordingBackupSettings>(`/v3/devices/${deviceId}/settings/recording/backup`, { method: 'PUT', body }).then(
      (res) => {
        if (!alive.current) return;
        const nextBackup = parseBackup(res.data);
        setSnapshot((prev) => (prev ? { ...prev, backup: nextBackup } : prev));
        setBackupDraft(nextBackup);
        setBackupPassword('');
        setBackupNotice(t('deviceSettings.recordBackupSaved'));
      },
      (err: unknown) => {
        if (!alive.current) return;
        setBackupError(err instanceof Error && err.message ? err.message : t('deviceSettings.recordBackupSaveFailed'));
      }
    ).finally(() => {
      if (alive.current) setBackupSaving(false);
    });
  };

  const backupRow = (label: string, value: string) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacing-6)', padding: 'var(--spacing-2) 0' }}>
      <span>{label}</span>
      <span className="text-secondary">{value || '—'}</span>
    </div>
  );

  // R3: constrain the MODE picker to the plan-allowed modes once entitlements are known &
  // complete. `none` (stop recording) is ALWAYS retained; the current selection is retained
  // even if outside the plan set (the picker's fallback <option> also guards this). null →
  // no constraint → full static list (no regression when unknown/incomplete/unattached).
  // R3:只在錄雲端時依方案夾(sdcard 兩種模式/全畫質都支援,不受方案限制)。對齊 iOS/Android。
  const allowedModes = snapshot.record_location === 'cloud' ? planRecordingModes(explain) : null;
  const modeOpts = modeOptions(t).filter(
    ([v]) => allowedModes == null || v === 'none' || v === snapshot.record_mode || allowedModes.includes(v)
  );
  const modeConstrained = allowedModes != null && modeOpts.length < modeOptions(t).length;
  // 錄影畫質(解析度)選單已移除(CAM-64):裝置解析度每產品固定、runtime 不可切換,選項與硬體脫鉤
  // (3K 機出現 4K、無 3K),顯示現值亦為假。端到端顯示/切換見 CAM-83;record_quality 存/送 plumbing 保留。

  return (
    <SectionCard title={t('deviceSettings.recording')}>
      {picker(t('deviceSettings.recordMode'), 'record_mode', modeOpts)}
      {modeConstrained && (
        <p className="text-secondary" style={{ margin: '0 0 var(--spacing-2)', fontSize: 'var(--font-size-sm)' }}>
          {t('deviceSettings.recordModePlanLimited')}
        </p>
      )}
      {picker(t('deviceSettings.recordLocation'), 'record_location', locationOptions(t))}
      {/* 錯誤緊貼選單：放在區塊最底下（週排程、備份下面）時，使用者只看到選項跳回去 */}
      {fieldError && (
        <p className="error-text" role="alert" style={{ margin: '0 0 var(--spacing-2)' }} data-testid="rec-field-error">
          {fieldError}
          {planFull && (
            <>
              {' '}
              <Link to="/account/subscription">{t('deviceSettings.cloudPlanManage')}</Link>
            </>
          )}
        </p>
      )}
      {/* 解析度:唯讀顯示裝置(機型登錄)最高解析度(CAM-83)。裝置解析度固定、runtime 不可切換 →
          只顯示不可選;機型未登錄 max_resolution 則不顯示。可選畫質選單已於 CAM-64 移除。 */}
      {maxResolution && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacing-6)', padding: 'var(--spacing-3) 0' }}>
          <span>{t('deviceSettings.recordResolution')}</span>
          <span className="text-secondary" data-testid="rec-resolution">{maxResolution}</span>
        </div>
      )}
      <WeeklyScheduleEditor
        deviceId={deviceId}
        attrId={REC_SCHEDULE}
        kind="recording"
        wsOpen={wsOpen}
        disabled={!canEdit}
        channels={channels}
      />

      {/* 手機攝影機沒有外部備份（FTP / 本機磁碟）；ls-loop MW */}
      {!phone && (
      <div style={{ borderTop: '1px solid var(--color-border)', marginTop: 'var(--spacing-3)', paddingTop: 'var(--spacing-3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacing-6)', padding: 'var(--spacing-2) 0' }}>
          <span>{t('deviceSettings.recordBackup')}</span>
          {canEdit ? (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
              <input type="checkbox" checked={backupDraft.enabled} disabled={backupSaving} onChange={(e) => setBackupField('enabled', e.target.checked)} />
              <span>{backupDraft.enabled ? t('common.on') : t('common.off')}</span>
            </label>
          ) : (
            <span className="text-secondary">{backupDraft.enabled ? t('common.on') : t('common.off')}</span>
          )}
        </div>

        {canEdit ? (
          <>
            <label style={{ display: 'grid', gridTemplateColumns: 'minmax(100px, 1fr) minmax(160px, 2fr)', alignItems: 'center', gap: 'var(--spacing-4)', padding: 'var(--spacing-2) 0' }}>
              <span>{t('deviceSettings.recordBackupType')}</span>
              <select value={backupDraft.type === 'none' ? 'ftp' : backupDraft.type} disabled={backupLocked || !backupDraft.enabled} onChange={(e) => setBackupField('type', e.target.value)}>
                {backupTypeOptions.map(([v, text]) => (
                  <option key={v} value={v}>{text}</option>
                ))}
              </select>
            </label>
            <label style={{ display: 'grid', gridTemplateColumns: 'minmax(100px, 1fr) minmax(160px, 2fr)', alignItems: 'center', gap: 'var(--spacing-4)', padding: 'var(--spacing-2) 0' }}>
              <span>{t('deviceSettings.recordBackupHost')}</span>
              <input value={backupDraft.host} disabled={backupLocked || !backupDraft.enabled || backupDraft.type === 'localfs'} onChange={(e) => setBackupField('host', e.target.value)} />
            </label>
            <label style={{ display: 'grid', gridTemplateColumns: 'minmax(100px, 1fr) minmax(160px, 2fr)', alignItems: 'center', gap: 'var(--spacing-4)', padding: 'var(--spacing-2) 0' }}>
              <span>{t('deviceSettings.recordBackupPort')}</span>
              <input type="number" min={0} max={65535} value={backupDraft.port || ''} disabled={backupLocked || !backupDraft.enabled || backupDraft.type === 'localfs'} onChange={(e) => setBackupField('port', Math.max(0, Math.min(65535, Number(e.target.value) || 0)))} />
            </label>
            <label style={{ display: 'grid', gridTemplateColumns: 'minmax(100px, 1fr) minmax(160px, 2fr)', alignItems: 'center', gap: 'var(--spacing-4)', padding: 'var(--spacing-2) 0' }}>
              <span>{t('deviceSettings.recordBackupRemoteDir')}</span>
              <input value={backupDraft.remote_dir} disabled={backupLocked || !backupDraft.enabled} onChange={(e) => setBackupField('remote_dir', e.target.value)} />
            </label>
            <label style={{ display: 'grid', gridTemplateColumns: 'minmax(100px, 1fr) minmax(160px, 2fr)', alignItems: 'center', gap: 'var(--spacing-4)', padding: 'var(--spacing-2) 0' }}>
              <span>{t('deviceSettings.recordBackupUsername')}</span>
              <input value={backupDraft.username} disabled={backupLocked || !backupDraft.enabled || backupDraft.type === 'localfs'} onChange={(e) => setBackupField('username', e.target.value)} />
            </label>
            <label style={{ display: 'grid', gridTemplateColumns: 'minmax(100px, 1fr) minmax(160px, 2fr)', alignItems: 'center', gap: 'var(--spacing-4)', padding: 'var(--spacing-2) 0' }}>
              <span>{t('deviceSettings.recordBackupPassword')}</span>
              <input type="password" value={backupPassword} disabled={backupLocked || !backupDraft.enabled || backupDraft.type === 'localfs'} placeholder={backupDraft.password_configured ? t('deviceSettings.recordBackupPasswordKeep') : ''} onChange={(e) => setBackupPassword(e.target.value)} />
            </label>
            <label style={{ display: 'grid', gridTemplateColumns: 'minmax(100px, 1fr) minmax(160px, 2fr)', alignItems: 'center', gap: 'var(--spacing-4)', padding: 'var(--spacing-2) 0' }}>
              <span>{t('deviceSettings.recordBackupServerName')}</span>
              <input value={backupDraft.server_name} disabled={backupLocked || !backupDraft.enabled || backupDraft.type !== 'ftps'} onChange={(e) => setBackupField('server_name', e.target.value)} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', padding: 'var(--spacing-2) 0' }}>
              <input type="checkbox" checked={backupDraft.disable_epsv} disabled={backupLocked || !backupDraft.enabled || backupDraft.type === 'localfs'} onChange={(e) => setBackupField('disable_epsv', e.target.checked)} />
              <span>{t('deviceSettings.recordBackupDisableEpsv')}</span>
            </label>
            <button className="btn-secondary" type="button" disabled={backupLocked} onClick={saveBackup} style={{ justifySelf: 'start', marginTop: 'var(--spacing-2)' }}>
              {backupSaving ? t('common.saving') : t('common.save')}
            </button>
          </>
        ) : (
          <>
            {backupRow(t('deviceSettings.recordBackupType'), backupTypeOptions.find(([v]) => v === backupDraft.type)?.[1] ?? backupDraft.type)}
            {backupRow(t('deviceSettings.recordBackupHost'), backupDraft.host)}
            {backupRow(t('deviceSettings.recordBackupRemoteDir'), backupDraft.remote_dir)}
            {backupRow(t('deviceSettings.recordBackupUsername'), backupDraft.username)}
            {backupRow(t('deviceSettings.recordBackupPassword'), backupDraft.password_configured ? t('deviceSettings.recordBackupPasswordSet') : '—')}
          </>
        )}
        {backupNotice && <p className="text-secondary" role="status" style={{ margin: 0 }}>{backupNotice}</p>}
        {backupError && <p className="error-text" role="alert" style={{ margin: 0 }}>{backupError}</p>}
      </div>
      )}

      {(phase !== 'idle' || cloudFlow?.step === 'checking') && (
        <p className="text-secondary" role="status" style={{ margin: 0 }}>
          <span aria-hidden>⏳</span>{' '}
          {cloudFlow?.step === 'checking'
            ? t('deviceSettings.cloudCheckingPlan')
            : phase === 'waiting-long'
              ? t('deviceSettings.waitingDevice')
              : t('deviceSettings.settingApplying')}
        </p>
      )}
      {notice && !locked && (
        <p className="text-secondary" role="status" style={{ margin: 0 }}>
          {notice}
        </p>
      )}

      {/* WSS-side members (non-DEC-09): RecordStatus STAT + ManualRecordLength knob */}
      {wsOpen && <ReadonlyRow label={t('deviceSettings.recordStatus')} attr={recStatus} render={(v) => (v === true || v === 1 ? t('deviceSettings.recording_active') : t('deviceSettings.recording_inactive'))} />}
      {wsOpen && canEdit && manualLen.status === 'ready' && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--spacing-6)', padding: 'var(--spacing-3) 0' }}>
          <span>
            {t('deviceSettings.manualRecordLength')}
            <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)', display: 'block' }}>
              {t('deviceSettings.manualRecordLengthHint')}{manualLen.setError ? ` · ${manualLen.setError}` : ''}
            </span>
          </span>
          <input
            aria-label={t('deviceSettings.manualRecordLength')}
            type="number"
            min={10}
            max={600}
            style={{ width: 100 }}
            value={manualDraft === '' ? String(typeof manualLen.value === 'number' ? manualLen.value : 60) : manualDraft}
            disabled={manualLen.setting}
            onChange={(e) => setManualDraft(e.target.value)}
            onBlur={() => {
              if (manualDraft === '') return;
              const n = Math.max(10, Math.min(600, Number(manualDraft) || 60));
              setManualDraft('');
              manualLen.set(n);
            }}
          />
        </div>
      )}

      {sdConfirm && (
        <ConfirmDialog
          spec={{
            title: t('deviceSettings.switchToSdcardTitle'),
            body: (
              <>
                <p style={{ marginTop: 0 }}>{t('deviceSettings.switchToSdcardBody1')}</p>
                <p style={{ marginBottom: 0 }}>{t('deviceSettings.switchToSdcardBody2')}</p>
              </>
            ),
            confirmLabel: t('deviceSettings.confirmSwitch'),
            danger: true,
          }}
          onCancel={() => setSdConfirm(null)} // 取消 → 無任何 wire 流量，picker 維持原值
          onConfirm={() => {
            const next = sdConfirm;
            setSdConfirm(null);
            submit(next);
          }}
        />
      )}

      {cloudFlow && cloudFlow.step === 'pick' && (
        // R1:多個可加入方案 → 顯示 plan list 讓使用者選(不自動挑第一個)。選一個 → 進 confirm。
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('deviceSettings.cloudPlanPickTitle')}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'grid', placeItems: 'center', zIndex: 1000, padding: 'var(--spacing-8)' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setCloudFlow(null);
          }}
        >
          <div className="card" style={{ width: 'min(420px, 100%)', display: 'grid', gap: 'var(--spacing-4)' }}>
            <h3 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>{t('deviceSettings.cloudPlanPickTitle')}</h3>
            <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
              {cloudFlow.subs.map((s) => (
                <button
                  key={s.subscription_id}
                  className="btn-secondary"
                  style={{ textAlign: 'left' }}
                  onClick={() => setCloudFlow({ step: 'confirm', next: cloudFlow.next, sub: s })}
                >
                  {s.plan_id} ({s.device_count}/{s.max_devices})
                </button>
              ))}
            </div>
            <button className="btn-ghost" onClick={() => setCloudFlow(null)}>
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {cloudFlow && (cloudFlow.step === 'confirm' || cloudFlow.step === 'attaching') && (
        <ConfirmDialog
          spec={{
            title: t('deviceSettings.cloudAttachTitle'),
            body: <p style={{ margin: 0 }}>{t('deviceSettings.cloudAttachBody')}</p>,
            confirmLabel: t('deviceSettings.cloudAttachConfirm'),
          }}
          busy={cloudFlow.step === 'attaching'}
          onCancel={() => setCloudFlow(null)} // D19-d：純 UI 還原，不呼叫任何寫入
          onConfirm={() => void attachAndApply()}
        />
      )}
    </SectionCard>
  );
}
